<script>
import { Comment, Fragment, Text, h } from 'vue'

function isTabVNode(vnode) {
	if (!vnode || typeof vnode !== 'object') return false
	if (typeof vnode.type === 'symbol') return false
	if (vnode.type === Text || vnode.type === Comment || vnode.type === Fragment) return false
	const props = vnode.props || {}
	if ('caption' in props) return true
	const name =
		(typeof vnode.type === 'object' && (vnode.type.name || vnode.type.__name)) ||
		''
	return name === 'Tab'
}

/** Vue иногда отдаёт Fragment / массив / comment между <Tab> */
function collectTabVNodes(raw) {
	const list = Array.isArray(raw) ? raw : raw ? [raw] : []
	const out = []
	const walk = (nodes) => {
		for (const vnode of nodes) {
			if (!vnode || typeof vnode !== 'object') continue
			if (vnode.type === Fragment || typeof vnode.type === 'symbol') {
				const ch = vnode.children
				if (Array.isArray(ch)) walk(ch)
				else if (ch) walk([ch])
				continue
			}
			if (vnode.type === Text || vnode.type === Comment) continue
			if (isTabVNode(vnode)) out.push(vnode)
		}
	}
	walk(list)
	return out
}

/** Контент вкладки без вложенного компонента Tab (иначе Vue: emitsOptions of null) */
function tabPanelChildren(slot) {
	if (!slot) return null
	const ch = slot.children
	if (ch && typeof ch === 'object' && !Array.isArray(ch) && typeof ch.default === 'function') {
		try {
			const nodes = ch.default()
			return Array.isArray(nodes) ? nodes.filter(Boolean) : nodes
		} catch (_) {
			return null
		}
	}
	if (Array.isArray(ch)) return ch.filter(Boolean)
	return ch || null
}

export default {
	name: 'Tabs',
	props: {
		height: {
			type: String,
			default: null
		},
		navClass: {
			type: String,
			default: 'nav nav-tabs nav-tabs-highlight'
		},
		activeTab: {
			type: Number,
			default: 0
		}
	},

	data() {
		return {
			active: Number(this.activeTab) || 0
		}
	},

	watch: {
		activeTab: {
			immediate: true,
			handler(value) {
				const n = Number(value)
				this.active = Number.isFinite(n) && n >= 0 ? n : 0
			}
		}
	},

	methods: {
		normalizeActive(slots) {
			let active = Number(this.active)
			if (!Number.isFinite(active) || active < 0 || active >= slots.length) {
				active = 0
			}
			const isSlotVisible = (slot) => {
				const props = slot.props || {}
				return !('visible' in props) || !!props.visible
			}
			if (slots.length && slots[active] && !isSlotVisible(slots[active])) {
				const firstVis = slots.findIndex(isSlotVisible)
				if (firstVis >= 0) active = firstVis
			}
			if (!slots.length) active = 0
			return active
		},
		selectTab(idx) {
			const n = Number(idx)
			if (!Number.isFinite(n) || n < 0) return
			if (this.active !== n) {
				this.active = n
				this.$emit('change', n)
			}
		}
	},

	render() {
		const tabItems = []
		const panelItems = []

		const styleB = {}
		if (this.height) {
			styleB['height'] = this.height
			styleB['overflow'] = 'scroll'
			styleB['overflow-x'] = 'hidden'
			styleB['overflow-y'] = 'auto'
		}

		const raw = this.$slots.default ? this.$slots.default() : []
		const slots = collectTabVNodes(raw)
		const active = this.normalizeActive(slots)

		let rightStarted = false
		const isCompact = String(this.navClass || '').includes('nav-tabs-compact')

		slots.forEach((slot, idx) => {
			const props = slot.props || {}
			const isVisible = !('visible' in props) || !!props.visible

			const caption = h('span', {
				innerHTML: props.caption
			})

			const items = [caption]

			if ('badge' in props) {
				items.push(h('span', { class: 'badge badge-secondary ml-1' }, props.badge))
			}

			const liClass = ['nav-item', 'unselectable']
			const liStyle = {
				display: isVisible ? undefined : 'none'
			}
			if (props.alignRight && isVisible && !rightStarted) {
				rightStarted = true
				liClass.push('nav-item-sep')
			}

			const fullTitle =
				typeof props.caption === 'string'
					? props.caption.replace(/<[^>]+>/g, '')
					: props.caption

			tabItems.push(
				h(
					'li',
					{
						key: `tab-nav-${idx}`,
						class: liClass.join(' '),
						style: liStyle
					},
					[
						h(
							'a',
							{
								class: idx === active ? 'nav-link active' : 'nav-link',
								title: fullTitle,
								href: '#',
								onClick: (e) => {
									e.preventDefault()
									e.stopPropagation()
									this.selectTab(idx)
								}
							},
							items
						)
					]
				)
			)

			const paneChildren = isVisible ? tabPanelChildren(slot) : null

			panelItems.push(
				h(
					'div',
					{
						key: `tab-pane-${idx}`,
						class: idx === active ? 'tab-pane show active' : 'tab-pane',
						style: isVisible ? undefined : { display: 'none' }
					},
					paneChildren
				)
			)
		})

		const tabs = h(
			'ul',
			{
				class: this.navClass,
				style: isCompact
					? {
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'flex-end',
							columnGap: '2px',
							rowGap: '2px'
						}
					: undefined
			},
			tabItems
		)
		const panels = h('div', { class: 'tab-content mt-1', style: styleB }, panelItems)

		return h('div', { class: isCompact ? 'tabs-compact-wrap' : undefined }, [tabs, panels])
	}
}
</script>

<style>
.nav-tabs .nav-item-sep {
	margin-left: 0.4rem;
	padding-left: 0.45rem;
	border-left: 2px solid rgba(0, 0, 0, 0.14);
}

.tabs-compact-wrap .nav-tabs-compact {
	gap: 0;
	border-bottom-width: 1px;
}

.tabs-compact-wrap .nav-tabs-compact > .nav-item {
	margin-bottom: 0;
}

.tabs-compact-wrap .nav-tabs-compact .nav-link {
	white-space: nowrap;
	padding: 0.22rem 0.4rem;
	font-size: 0.78rem;
	font-weight: 600;
	line-height: 1.15;
	height: auto !important;
	min-height: 0;
	border-radius: 6px 6px 0 0;
}

.tabs-compact-wrap .nav-tabs-compact .nav-link > .badge {
	font-size: 0.62rem;
	padding: 0.08rem 0.28rem;
	vertical-align: middle;
	margin-left: 0.2rem !important;
}

.tabs-compact-wrap .nav-tabs-compact.nav-tabs-highlight .nav-link:before {
	display: none;
}

.tabs-compact-wrap .nav-tabs-compact .nav-item-sep .nav-link {
	opacity: 0.92;
}
</style>
